import React, { useState } from 'react';
import Card from '../Shared/Card';
import Input from '../Shared/Input';
import Button from '../Shared/Button';
import Dropdown from '../Shared/Dropdown';
import { LANGUAGE_OPTIONS, TONE_OPTIONS, LENGTH_OPTIONS } from '../../utils/constants';
import AIService from '../../services/aiService';
import SettingsService from '../../services/settingsService';

const PostForm = ({ onPostGenerated }) => {
  const preferences = SettingsService.getPreferences();

  // Load form data from sessionStorage or use defaults
  const [formData, setFormData] = useState(() => {
    const saved = sessionStorage.getItem('postFormData');
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      title: '',
      language: preferences.language || 'english',
      tone: preferences.tone || '',
      customTopics: '',
      lengthType: preferences.lengthType || 'medium',
      lengthMin: '',
      lengthMax: ''
    };
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    const newData = {
      ...formData,
      [field]: value
    };
    setFormData(newData);

    // Save to sessionStorage
    sessionStorage.setItem('postFormData', JSON.stringify(newData));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };


  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'العنوان مطلوب';
    } else if (formData.title.length < 5) {
      newErrors.title = 'العنوان يجب أن يكون 5 أحرف على الأقل';
    }

    if (!formData.tone) {
      newErrors.tone = 'الرجاء اختيار النبرة';
    }

    if (!formData.customTopics.trim()) {
      newErrors.topics = 'الرجاء إدخال المواضيع والكلمات المفتاحية';
    } else if (formData.customTopics.length < 3) {
      newErrors.topics = 'المواضيع يجب أن تكون 3 أحرف على الأقل';
    }

    if (formData.lengthType === 'custom') {
      if (!formData.lengthMin || formData.lengthMin < 50) {
        newErrors.lengthMin = 'الحد الأدنى يجب أن يكون 50 كلمة على الأقل';
      }
      if (!formData.lengthMax || formData.lengthMax > 2000) {
        newErrors.lengthMax = 'الحد الأقصى يجب أن يكون أقل من 2000 كلمة';
      }
      if (formData.lengthMin && formData.lengthMax && parseInt(formData.lengthMin) >= parseInt(formData.lengthMax)) {
        newErrors.lengthMax = 'الحد الأقصى يجب أن يكون أكبر من الحد الأدنى';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await AIService.generatePost({
        title: formData.title,
        language: formData.language,
        tone: formData.tone,
        customTopics: formData.customTopics,
        lengthType: formData.lengthType,
        lengthMin: formData.lengthType === 'custom' ? parseInt(formData.lengthMin) : null,
        lengthMax: formData.lengthType === 'custom' ? parseInt(formData.lengthMax) : null
      });

      if (response.success) {
        // Pass both the generated data and the form metadata
        onPostGenerated(response.data, {
          title: formData.title,
          tone: formData.tone,
          customTopics: formData.customTopics,
          language: formData.language,
          lengthType: formData.lengthType,
          lengthMin: formData.lengthType === 'custom' ? parseInt(formData.lengthMin) : null,
          lengthMax: formData.lengthType === 'custom' ? parseInt(formData.lengthMax) : null
        });
      } else {
        alert('Error occurred: ' + response.error?.message);
      }
    } catch (error) {
      alert('Failed to generate post: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title Input */}
        <Input
          label="عنوان المنشور *"
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          placeholder="مثال: أفضل 5 طرق لتعلم البرمجة أو أفضل ممارسات ترحيل S/4HANA"
          required
          maxLength={200}
          showCounter
          error={errors.title}
        />

        {/* Language Selection */}
        <Dropdown
          label="لغة المنشور"
          value={formData.language}
          onChange={(value) => handleInputChange('language', value)}
          options={LANGUAGE_OPTIONS}
          placeholder="اختر لغة المنشور"
          required
        />

        {/* Tone Selection */}
        <Dropdown
          label="النبرة والأسلوب"
          value={formData.tone}
          onChange={(value) => handleInputChange('tone', value)}
          options={TONE_OPTIONS}
          placeholder="اختر نبرة المنشور"
          required
          error={errors.tone}
        />

        {/* Topics & Keywords */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              المواضيع والكلمات المفتاحية <span className="text-red-500">*</span>
            </label>
            {preferences.favoriteTopics && (
              <button
                type="button"
                onClick={() => handleInputChange('customTopics', preferences.favoriteTopics)}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                استخدم المواضيع المفضلة
              </button>
            )}
          </div>
          <Input
            value={formData.customTopics}
            onChange={(e) => handleInputChange('customTopics', e.target.value)}
            placeholder="مثال: SAP S/4HANA، الترحيل، ERP، إدارة المشاريع، البرمجة، القيادة"
            maxLength={300}
            showCounter
            error={errors.topics}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            أدخل المواضيع والكلمات المفتاحية ذات الصلة (مفصولة بفواصل) - هذه توجه الذكاء الاصطناعي لإنشاء محتوى مركّز
          </p>
        </div>

        {/* Length Selection */}
        <Dropdown
          label="طول المنشور"
          value={formData.lengthType}
          onChange={(value) => handleInputChange('lengthType', value)}
          options={LENGTH_OPTIONS}
          placeholder="اختر طول المنشور"
          required
        />

        {/* Custom Length Inputs */}
        {formData.lengthType === 'custom' && (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="الحد الأدنى (كلمة)"
              type="number"
              value={formData.lengthMin}
              onChange={(e) => handleInputChange('lengthMin', e.target.value)}
              placeholder="50"
              min="50"
              max="1000"
              error={errors.lengthMin}
            />
            <Input
              label="الحد الأقصى (كلمة)"
              type="number"
              value={formData.lengthMax}
              onChange={(e) => handleInputChange('lengthMax', e.target.value)}
              placeholder="500"
              min="100"
              max="2000"
              error={errors.lengthMax}
            />
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            loading={loading}
            variant="primary"
            size="large"
            className="px-8"
          >
            {loading ? 'جاري التوليد...' : 'توليد المنشور'}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default PostForm;